import { all, put, select, takeLatest } from 'redux-saga/effects';
import { ConsultsActionTypes } from '@store/consults/consults-actions';
import { AlertActionType } from '@store/alert/alert-action';
import createDialogueBox from '@api/consults/dialogue-box-create';
import getConsultDatatable from '@api/consults/datatable';
import storeConsult from '@api/consults/consult-store';
import storeDialogueBox from '@api/consults/dialogue-box-store';
import uploadFileApi from '@api/consults/upload-file';
import { consultsStore } from '@store/consults/consults-store';
import { AuthActionTypes } from '@store/auth/auth-actions';
import { checkVerifyCodeWatcher } from '@store/auth/auth-middleware';
import { authStore } from '@store/auth/auth-store';
import ReactGA from 'react-ga4';
import { homeStore } from '@store/home-screen/home-store';
import electronicPrescriptionApi from '@api/consults/electronic-prescription';
import { PharmaciesActionTypes } from '@store/pharmacies/pharmacies-actions';
import routes from '@routes';
import getParseRoute from '@utils/helpers/parse-route';
import getDictionary from '@dictionaries-client';

function* loadDatatableInfoDataWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { consult: { messages } } } = getDictionary(locale);
  try {
    const { hashUserId } = yield select(consultsStore);
    const { isLoggedIn } = yield select(authStore);

    if (isLoggedIn) {
      const createResponse: { extra: { allowToAnswer: boolean, openData: any } } = yield createDialogueBox({ hashUserId: hashUserId, cache: 'no-cache' });

      if (createResponse) {
        yield put({
          type: ConsultsActionTypes.SET_CONSULTS_OPEN_DATA,
          data: {
            allowToAnswer: createResponse?.extra?.allowToAnswer,
            openData: createResponse?.extra?.openData,
            checkOpenData: !(createResponse?.extra?.openData),
          },
        });
      }
    }
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.default,
      severity: 'danger',
    });
  }
}

function* loadDataTableWatcher() {
  const { hashUserId, currentPage } = yield select(consultsStore);
  const { locale } = yield select(homeStore);
  const { api: { consult: { messages } } } = getDictionary(locale);
  try {
    const datatableResponse: { data: { data: any, last_page: number, current_page: number } } = yield getConsultDatatable({ hashUserId: hashUserId, page: currentPage + 1, cache: 'no-cache' });
    if (datatableResponse) {
      yield put({
        type: ConsultsActionTypes.SET_DATATABLE,
        data: {
          data: datatableResponse?.data?.data,
          lastPage: datatableResponse?.data?.last_page,
          currentPage: datatableResponse?.data?.current_page,
        },
      });
    } else {
      yield put({
        type: ConsultsActionTypes.SET_DATATABLE,
        data: {
          data: [],
          lastPage: 0,
          currentPage: 0,
        },
      });
    }
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.default,
      severity: 'danger',
    });
  }
}

function* storeDialogueBoxWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { consult: { messages } } } = getDictionary(locale);
  try {
    const { hashUserId, content, fileResponse } = yield select(consultsStore);

    const response: { extra: { defaultDrText: any, userInfo: any } } = yield storeDialogueBox({
      hashUserId: hashUserId,
      content: content || fileResponse?.file,
      file: {
        '1': fileResponse,
      },
      cache: 'no-cache',
    });

    if (response) {
      yield put({
        type: ConsultsActionTypes.SET_CHAT_CONTENT,
        content: null,
      });
      yield loadDataTableWatcher();
    }

    yield put({
      type: ConsultsActionTypes.STORE_DIALOG_BOX_LOADING,
      storeLoading: false,
    });
    yield put({
      type: ConsultsActionTypes.SET_FILE_INITIAL_STATE,
    });
  } catch (error: any) {
    yield put({
      type: ConsultsActionTypes.STORE_DIALOG_BOX_LOADING,
      storeLoading: false,
    });
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.send_message_error,
      severity: 'danger',
    });
  }
}

function* storeConsultWatcher() {
  const { navigation, locale } = yield select(homeStore);
  const { api: { consult: { messages } } } = getDictionary(locale);
  try {
    const { hashUserId, content } = yield select(consultsStore);
    if (content) {
      const response: { data: { route_key: string }, extra: { defaultDrText: any, userInfo: any } } = yield storeConsult({
        hashUserId: hashUserId,
        content: content,
        cache: 'no-cache',
      });

      if (response) {
        yield put({
          type: ConsultsActionTypes.SET_CHAT_CONTENT,
          content: null,
        });

        yield loadDatatableInfoDataWatcher();
        yield loadDataTableWatcher();
        ReactGA.ga('send', 'event', 'consult', 'create', 'consult make order');
        yield navigation.replace(getParseRoute({ pathname: `/financials/order/${response?.data?.route_key}`, locale: locale }));
      }
    } else {
      yield put({
        type: AlertActionType.SHOW_ALERT,
        text: messages.text_messages_require,
        severity: 'danger',
      });
    }

    yield put({
      type: ConsultsActionTypes.STORE_DIALOG_BOX_LOADING,
      storeLoading: false,
    });
  } catch (error: any) {
    yield put({
      type: ConsultsActionTypes.STORE_DIALOG_BOX_LOADING,
      storeLoading: false,
    });
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.send_message_error,
      severity: 'danger',
    });
  }
}

function* submitConsultUserLoggedInWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { consult: { messages } } } = getDictionary(locale);
  try {
    const { auth } = yield select(consultsStore);

    if (!auth.isLoggedIn) {
      yield checkVerifyCodeWatcher();
      yield loadDatatableInfoDataWatcher();
    }

    yield submitConsultWatcher();
  } catch (error: any) {
    yield put({
      type: AuthActionTypes.SET_CONFIRM_LOADING,
      data: {
        confirmLoading: false,
      },
    });
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.default,
      severity: 'danger',
    });
  }
}

function* submitConsultUserIsCompletedWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { consult: { messages } } } = getDictionary(locale);
  try {
    const { auth } = yield select(consultsStore);

    if (auth.isLoggedIn && !auth.isCompleted) {
      yield put({
        type: AuthActionTypes.COMPLETE_CLIENT_PROFILE,
      });
      yield loadDatatableInfoDataWatcher();
    }

    yield submitConsultWatcher();
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.edit_profile_error,
      severity: 'danger',
    });
  }
}

function* submitConsultWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { consult: { messages } } } = getDictionary(locale);
  try {
    const { content, allowToAnswer, openData, auth } = yield select(consultsStore);

    if (auth.isLoggedIn && !auth.isCompleted) {
      yield put({
        type: AuthActionTypes.SET_COMPLETE_PROFILE_MODAL,
        data: {
          completeProfileModal: true,
        },
      });
      return;
    }

    if (content) {
      if (openData) {
        if (openData.status === '3') {
          yield put({
            type: AlertActionType.SHOW_ALERT,
            text: messages.open_chat_error,
            severity: 'danger',
          });
          yield put({
            type: ConsultsActionTypes.SET_START_CHAT,
            startChat: false,
          });
          return;
        } else if (openData.status === '5') {
          yield put({
            type: AlertActionType.SHOW_ALERT,
            text: messages.wait_for_acceptance_error,
            severity: 'danger',
          });
          yield put({
            type: ConsultsActionTypes.SET_START_CHAT,
            startChat: false,
          });
          return;
        }
      }
      if (allowToAnswer && !!openData) {
        yield storeDialogueBoxWatcher();
      } else {
        yield storeConsultWatcher();
      }
    }
  } catch (error: any) {
    yield put({
      type: ConsultsActionTypes.STORE_DIALOG_BOX_LOADING,
      storeLoading: false,
    });
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.send_message_error,
      severity: 'danger',
    });
  }
}

function* setChatFileWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { consult: { messages } } } = getDictionary(locale);
  try {
    const { file, fileType } = yield select(consultsStore);

    const response: { data: any } = yield uploadFileApi({
      file: file,
      type: fileType,
      cache: 'no-cache',
    });

    if (response) {
      yield put({
        type: ConsultsActionTypes.SET_FILE_RESPONSE,
        data: {
          fileResponse: response,
        },
      });
    }
  } catch (error: any) {
    yield put({
      type: ConsultsActionTypes.STORE_DIALOG_BOX_LOADING,
      storeLoading: false,
      fileLoading: false,
    });
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.upload_file_error,
      severity: 'danger',
    });
  }
}

function* getElectronicPrescriptionWatcher() {
  const { locale } = yield select(homeStore);
  const { api: { consult: { messages } } } = getDictionary(locale);
  try {
    const { navigation, locale } = yield select(homeStore);
    const { consultDialogueId } = yield select(consultsStore);
    const response: { data: {electronic_prescription: any} } = yield electronicPrescriptionApi({ id: consultDialogueId, cache: 'no-cache' });
    yield put({
      type: PharmaciesActionTypes.SET_CONSULT_PRESCRIPTION,
      data: {
        insurance_type: response?.data?.electronic_prescription?.insurance_type?.value,
        doctor_name: response?.data?.electronic_prescription?.doctor_name,
        description: response?.data?.electronic_prescription?.description,
        national_code: response?.data?.electronic_prescription?.national_code,
        tracking_code: response?.data?.electronic_prescription?.tracking_code,
        consult_dialogue_id: consultDialogueId,
      },
    });
    yield put({
      type: PharmaciesActionTypes.SET_CITY_MODAL,
      data: {
        open: false,
      },
    });
    yield navigation.push(getParseRoute({ pathname: routes['route.pharmacies.checkout-electronic-prescription'], locale: locale }));
  } catch (error: any) {
    yield put({
      type: AlertActionType.SHOW_ALERT,
      text: error?.message || messages.default,
      severity: 'danger',
    });
  }
  yield put({ type: ConsultsActionTypes.SET_ELECTRONIC_PRESCRIPTION });
}

function* consultsMiddleware() {
  yield all([
    takeLatest(ConsultsActionTypes.GET_DATATABLE_INFO, loadDatatableInfoDataWatcher),
    takeLatest(ConsultsActionTypes.STORE_DIALOG_BOX, storeDialogueBoxWatcher),
    takeLatest(ConsultsActionTypes.STORE_CONSULT, storeConsultWatcher),
    takeLatest(ConsultsActionTypes.SUBMIT_CONSULT_WITH_LOGIN, submitConsultUserLoggedInWatcher),
    takeLatest(ConsultsActionTypes.SUBMIT_CONSULT_WITH_COMPLETE_PROFILE, submitConsultUserIsCompletedWatcher),
    takeLatest(ConsultsActionTypes.SUBMIT_CONSULT, submitConsultWatcher),
    takeLatest(ConsultsActionTypes.SET_CHAT_FILE, setChatFileWatcher),
    takeLatest(ConsultsActionTypes.SET_FILE_RESPONSE, storeDialogueBoxWatcher),
    takeLatest(ConsultsActionTypes.GET_EXTENDED_DATATABLE, loadDatatableInfoDataWatcher),
    takeLatest(ConsultsActionTypes.GET_DATATABLE, loadDataTableWatcher),
    takeLatest(ConsultsActionTypes.GET_ELECTRONIC_PRESCRIPTION, getElectronicPrescriptionWatcher),
  ]);
}

export default consultsMiddleware;
